"use client"

import {
  formDictionary,
  UpdateCustomerForm,
  useCustomerContext,
  useGetCustomer,
} from "@unlikelystudio/react-ecommerce-hooks"

import type { PropsWithClassName } from "~/@types/generics"
import { ALERT_CODES } from "~/lib/constants"
import { CUSTOMER_METAFIELD_INPUTS, getCustomerMetafieldValue, type MetafieldInput } from "~/lib/shopify/constants"
import useMutationErrors from "~/hooks/account/useMutationErrors"
import useSetMetafields from "~/hooks/account/useSetMetafields"
import { useAlerts } from "~/hooks/useAlerts"
import { FORM_NAMES, FormTemplate } from "~/components/forms/FormTemplate"

type UpdateCustomerInformationsTemplateProps = PropsWithClassName<{}>
function UpdateCustomerInformationsTemplate({ className }: UpdateCustomerInformationsTemplateProps) {
  const onError = useMutationErrors()
  const triggerAlert = useAlerts()

  const onSuccess = () => {
    triggerAlert(ALERT_CODES.ENTITY_SAVED)
  }

  const { data: userData } = useGetCustomer({
    refetchOnMount: true,
    keepPreviousData: true,
    retry: 0,
  })
  const { customerAccessToken } = useCustomerContext()

  const { mutate: setMetafields, isLoading } = useSetMetafields({
    onSuccess,
    /**
     * Return onSuccess as the initial update customer was successful
     * If you reach this state
     */
    onError: onSuccess,
  })

  const customerBirthday = userData ? getCustomerMetafieldValue(userData?.metafields, "BIRTHDAY") : null
  const customerGender = userData ? getCustomerMetafieldValue(userData?.metafields, "GENDER") : null

  return (
    <FormTemplate
      className={className}
      withRequiredText
      isLoading={isLoading}
      dictionary={formDictionary}
      formName={FORM_NAMES.UPDATE_INFORMATION}
    >
      <UpdateCustomerForm
        hideFields={["password", "repeatPassword", "gender"]}
        defaultValues={{ birthdate: customerBirthday ?? "", gender: customerGender ?? "" }}
        handleAdditionalFields={({ birthdate, gender }) => {
          const metafields: MetafieldInput[] = []

          if (birthdate) {
            metafields.push(CUSTOMER_METAFIELD_INPUTS.BIRTHDAY(birthdate))
          }
          if (gender) {
            metafields.push(CUSTOMER_METAFIELD_INPUTS.GENDER(gender))
          }

          if (metafields.length > 0) {
            return setMetafields({
              metafields,
              customerAccessToken,
            })
          }
          // If there is no need to upsert metafield, trigger onSuccess for updateCustomer mutation
          onSuccess()
        }}
        mutationOptions={{
          onError,
        }}
      />
    </FormTemplate>
  )
}

export { UpdateCustomerInformationsTemplate }
